import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
    Notification,
    NotificationStats,
    NotificationPreferences,
    NotificationQueryParams,
    UpdatePreferencesDto,
    SecurityEvent,
    NotificationCategory,
} from '@/app/shared/interfaces/notification.interface';
import { ResponsePaginatedWrapper, ResponseSingleWrapper } from '@/app/shared/dto';

/**
 * Service for making HTTP calls to the backend notifications API
 * Extends ApiService for consistent error handling and response wrapping
 * Matches backend NotificationsController endpoints
 */
@Injectable({ providedIn: 'root' })
export class NotificationApiService extends ApiService {
    protected get basePath(): string {
        return '/notifications';
    }

    /**
     * Get all notifications for current user with pagination and filters
     * GET /notifications
     */
    getNotifications(params?: NotificationQueryParams): Observable<ResponsePaginatedWrapper<Notification>> {
        return this.getPaginated<Notification>('', params as any);
    }

    /**
     * Get notification statistics
     * GET /notifications/stats
     */
    getStats(): Observable<NotificationStats> {
        return this.get<NotificationStats>('/stats').pipe(map((response) => response.data));
    }

    /**
     * Get notification preferences
     * GET /notifications/preferences
     */
    getPreferences(): Observable<NotificationPreferences> {
        return this.get<NotificationPreferences>('/preferences').pipe(map((response) => response.data));
    }

    /**
     * Update notification preferences
     * PATCH /notifications/preferences
     */
    updatePreferences(preferences: UpdatePreferencesDto): Observable<NotificationPreferences> {
        return this.patch<NotificationPreferences>('/preferences', preferences).pipe(map((response) => response.data));
    }

    /**
     * Search notifications
     * POST /notifications/search
     */
    searchNotifications(query: string): Observable<Notification[]> {
        return this.post<Notification[]>('/search', { query }).pipe(map((response) => response.data));
    }

    /**
     * Get security events with filtering and pagination
     * GET /notifications/security
     * Note: Has special nested response structure from backend
     */
    getSecurityEvents(params?: {
        period?: '1d' | '7d' | '30d' | 'all';
        page?: number;
        limit?: number;
    }): Observable<{ data: SecurityEvent[]; meta: any }> {
        // Build HttpParams if params provided
        let httpParams: HttpParams | undefined;
        if (params) {
            httpParams = new HttpParams();
            if (params.period) {
                httpParams = httpParams.set('period', params.period);
            }
            if (params.page !== undefined) {
                httpParams = httpParams.set('page', params.page.toString());
            }
            if (params.limit !== undefined) {
                httpParams = httpParams.set('limit', params.limit.toString());
            }
        }

        return this.http
            .get<any>(this.buildSecurityUrl('/security'), { params: httpParams, withCredentials: true })
            .pipe(
                map((response: any) => {
                    // Handle nested data structure from backend (data.data)
                    if (response.data && response.data.data) {
                        return {
                            data: response.data.data,
                            meta: response.data.meta,
                        };
                    }
                    // Fallback for direct structure
                    return response.data || response;
                })
            );
    }

    /**
     * Get notifications by category
     * GET /notifications/category/:category
     */
    getByCategory(category: NotificationCategory): Observable<Notification[]> {
        return this.get<Notification[]>(`/category/${category}`).pipe(map((response) => response.data));
    }

    /**
     * Get a specific notification by ID
     * GET /notifications/:id
     */
    getNotification(id: string): Observable<Notification> {
        return this.get<Notification>(`/${id}`).pipe(map((response) => response.data));
    }

    /**
     * Mark notification as read
     * PATCH /notifications/:id/read
     */
    markAsRead(id: string): Observable<Notification> {
        return this.patch<Notification>(`/${id}/read`, {}).pipe(map((response) => response.data));
    }

    /**
     * Mark all notifications as read
     * PATCH /notifications/mark-all-read
     */
    markAllAsRead(): Observable<void> {
        return this.patch<void>('/mark-all-read', {}).pipe(map(() => undefined));
    }

    /**
     * Delete a notification
     * DELETE /notifications/:id
     */
    deleteNotification(id: string): Observable<void> {
        return this.deleteVoid(`/${id}`);
    }

    /**
     * Delete all notifications for the current user
     * DELETE /notifications
     */
    deleteAllNotifications(isArchived?: string): Observable<void> {
        const params = isArchived !== undefined ? `?isArchived=${isArchived}` : '';
        return this.deleteVoid(`${params}`);
    }

    /**
     * Archive a notification
     * PATCH /notifications/:id/archive
     */
    archiveNotification(id: string): Observable<void> {
        return this.patch<void>(`/${id}/archive`, {}).pipe(map(() => undefined));
    }

    /**
     * Archive all notifications for the current user
     * PATCH /notifications/archive-all
     */
    archiveAllNotifications(): Observable<void> {
        return this.patch<void>('/archive-all', {}).pipe(map(() => undefined));
    }

    /**
     * Helper to build URL for security endpoint
     * Security endpoint has special response structure
     */
    private buildSecurityUrl(endpoint: string): string {
        const base = this.basePath.replace(/^\/|\/$/g, '');
        const path = endpoint.replace(/^\/|\/$/g, '');
        return `${this.apiUrl}/${base}/${path}`;
    }
}
